import React, { useState, useEffect, useCallback } from "react"
import { Form, Row, Col, Button } from "react-bootstrap"
import { useLocation, useNavigate } from "react-router-dom"
import Layout from "../components/layout/Layout"
import useAuthAxios from "../hooks/useAuthAxios"
import { useAuth } from "../context/AuthContext"
import { filterEmptyFields } from "../utils/searchUtils"
import { usePersistedState } from "../hooks/usePersistedState"
import { formatDateForDisplay, formatDateForApi } from "../utils/dateUtils"
import { toast } from "react-toastify"
import DataTable from "../components/common/DataTable"
import DateTimePicker from "../components/common/DateTimePicker"

const RepairsList = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { get, loading, error } = useAuthAxios()
  const { getAppLookupValue, isAdmin, appLookups, isGarage } = useAuth()
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  // Use persisted state for search results and criteria
  const [repairs, setRepairs] = usePersistedState("repairs", [])
  const [hasSearched, setHasSearched] = useState(false)
  const [searchCriteria, setSearchCriteria] = usePersistedState("searchCriteria", {
    RepairID: null,
    Status: [],
    IssueDateTimeFrom: null,
    IssueDateTimeTo: null,
    CompletionDateTimeFrom: null,
    CompletionDateTimeTo: null,
    InsuredSurname: "",
    InsuranceCompany: "",
    ExpertID: null,
    GarageID: null,
    VehiclePlateNumber: ""
  })

  // Clear persisted state when navigating away from repairs routes
  useEffect(() => {
    return () => {
      // Get the next route from the browser's history state
      const nextPath = window.location.pathname

      // Only clear if navigating away from repairs routes
      if (!nextPath.startsWith("/repairs")) {
        // Clear React state
        setRepairs([])
        setSearchCriteria({
          RepairID: null,
          Status: [],
          IssueDateTimeFrom: null,
          IssueDateTimeTo: null,
          ExpertAssignmentDateFrom: null,
          ExpertAssignmentDateTimeTo: null,
          CompletionDateTimeFrom: null,
          CompletionDateTimeTo: null,
          Surname: "",
          ExpertID: null,
          VehiclePlateNumber: ""
        })

        // Clear localStorage
        localStorage.removeItem("repairs")
        localStorage.removeItem("searchCriteria")
      }
    }
  }, [setRepairs, setSearchCriteria])

  // Get status options from appLookups
  const statusOptions = appLookups
    ? appLookups.filter(lookup => lookup.Table === "Status").sort((a, b) => a.Code - b.Code)
    : []

  // Handle search form submission
  const handleSearch = useCallback(
    async e => {
      if (e) {
        e.preventDefault()
      }
      try {
        setHasSearched(true)
        const filteredCriteria = filterEmptyFields(searchCriteria)
        filteredCriteria.CompletionDateTimeFrom = formatDateForApi(filteredCriteria.CompletionDateTimeFrom)
        filteredCriteria.CompletionDateTimeTo = formatDateForApi(filteredCriteria.CompletionDateTimeTo)
        filteredCriteria.ExpertAssignmentDateFrom = formatDateForApi(
          filteredCriteria.ExpertAssignmentDateFrom
        )
        filteredCriteria.ExpertAssignmentDateTimeTo = formatDateForApi(
          filteredCriteria.ExpertAssignmentDateTimeTo
        )
        filteredCriteria.IssueDateTimeFrom = formatDateForApi(filteredCriteria.IssueDateTimeFrom)
        filteredCriteria.IssueDateTimeTo = formatDateForApi(filteredCriteria.IssueDateTimeTo)
        const response = await get("/repairs", { params: filteredCriteria })
        const items = response.data || []
        setRepairs(items)
      } catch (err) {
        console.error("Failed to fetch repairs:", err)
        if (err.response?.status !== 404) {
          // Don't show toast for no results
          toast.error("Αποτυχία φόρτωσης επισκευών. Παρακαλώ, προσπαθήστε ξανά.")
        }
        setRepairs([])
      }
    },
    [get, searchCriteria, setRepairs]
  )

  // Handle status from navigation state
  useEffect(() => {
    const handleInitialLoad = async () => {
      if (!initialLoadDone && location.state?.status !== undefined) {
        try {
          const status = location.state.status
          // Only set status if it's different from current
          if (!searchCriteria.Status.length || searchCriteria.Status[0].short !== status) {
            await setSearchCriteria(prev => ({
              ...prev,
              Status: [{ short: status }]
            }))
          }
        } catch (error) {
          console.error("Failed to set initial status:", error)
          toast.error("Αποτυχία αρχικοποίησης αναζήτησης.")
        } finally {
          // Clear the state after using it
          navigate(location.pathname, { replace: true, state: {} })
          setInitialLoadDone(true)
        }
      } else {
        setInitialLoadDone(true)
      }
    }

    handleInitialLoad()
  }, [location.state, location.pathname, initialLoadDone, navigate, setSearchCriteria, searchCriteria.Status])

  // Handle search when status changes
  useEffect(() => {
    if (initialLoadDone && searchCriteria.Status.length > 0 && undefined !== location.state?.status) {
      handleSearch()
    }
  }, [searchCriteria.Status, initialLoadDone, handleSearch, location.state?.status])

  // Handle reset
  const handleReset = useCallback(() => {
    setSearchCriteria({
      RepairID: null,
      Status: [],
      IssueDateTimeFrom: null,
      IssueDateTimeTo: null,
      ExpertAssignmentDateFrom: null,
      ExpertAssignmentDateTimeTo: null,
      CompletionDateTimeFrom: null,
      CompletionDateTimeTo: null,
      Surname: "",
      ExpertID: null,
      VehiclePlateNumber: ""
    })
    setRepairs([])
    setHasSearched(false)
  }, [setSearchCriteria, setRepairs])

  // Handle search criteria changes
  const handleCriteriaChange = (field, value) => {
    // For "from" date fields, set time to 00:00:00
    if (field.endsWith("From") && value) {
      const date = new Date(value)
      date.setHours(0, 0, 0, 0)
      value = date.toISOString()
    }
    // For "to" date fields, set time to 23:59:59
    else if (field.endsWith("To") && value) {
      const date = new Date(value)
      date.setHours(23, 59, 59, 999)
      value = date.toISOString()
    }

    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const getStatusLabel = status => {
    const statusName = getAppLookupValue("Status", status)
    return statusName ?? "Unknown"
  }

  // Add custom styles to match dashboard colors exactly
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
            .badge.bg-custom-secondary { background-color: #6c757d !important; }
            .badge.bg-custom-warning { background-color: #ffb81e !important; }
            .badge.bg-custom-primary { background-color: #0d6efd !important; }
            .badge.bg-custom-success { background-color: #198754 !important; }
            .badge.bg-custom-danger { background-color: #dc3545 !important; }
        `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  const handleViewRepair = id => {
    navigate(`/repairs/${id}`)
  }

  return (
    <Layout>
      <Form onSubmit={handleSearch}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Επισκευές</h5>
        </div>
        <Row className="g-3">
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">ID Επισκευής</Form.Label>
              <Form.Control
                type="number"
                value={searchCriteria.RepairID || ""}
                onChange={e =>
                  handleCriteriaChange("RepairID", e.target.value ? parseInt(e.target.value, 10) : null)
                }
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Αρ. Φακέλου</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.ClaimNo || ""}
                onChange={e => handleCriteriaChange("ClaimNo", e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Κατάσταση</Form.Label>
              <Form.Select
                value={searchCriteria.Status.length > 0 ? searchCriteria.Status[0].short : ""}
                onChange={e =>
                  handleCriteriaChange(
                    "Status",
                    e.target.value ? [{ short: parseInt(e.target.value, 10) }] : []
                  )
                }
                disabled={loading}>
                <option value="">Όλες</option>
                {statusOptions.map(status => (
                  <option key={status.Code} value={status.Code}>
                    {status.Name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Καταχώρησης Από</Form.Label>
              <DateTimePicker
                value={searchCriteria.IssueDateTimeFrom}
                onChange={value => handleCriteriaChange("IssueDateTimeFrom", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Καταχώρησης Έως</Form.Label>
              <DateTimePicker
                value={searchCriteria.IssueDateTimeTo}
                onChange={value => handleCriteriaChange("IssueDateTimeTo", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Ολοκλήρωσης Από</Form.Label>
              <DateTimePicker
                value={searchCriteria.CompletionDateTimeFrom}
                onChange={value => handleCriteriaChange("CompletionDateTimeFrom", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ημ. Ολοκλήρωσης Έως</Form.Label>
              <DateTimePicker
                value={searchCriteria.CompletionDateTimeTo}
                onChange={value => handleCriteriaChange("CompletionDateTimeTo", value)}
                disabled={loading}
                showClearButton={true}
                enableTime={false}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Επώνυμο Ιδιοκτήτη</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.InsuredSurname}
                onChange={e => handleCriteriaChange("InsuredSurname", e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ασφαλιστική Εταιρεία</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.InsuranceCompany}
                onChange={e => handleCriteriaChange("InsuranceCompany", e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>

          {isAdmin() && (
            <>
              <Col lg={3} md={6} xs={12}>
                <Form.Group className="h-100">
                  <Form.Label className="d-block text-nowrap mb-1">ID Πραγματογνώμονα</Form.Label>
                  <Form.Control
                    type="number"
                    value={searchCriteria.ExpertID || ""}
                    onChange={e =>
                      handleCriteriaChange("ExpertID", e.target.value ? parseInt(e.target.value, 10) : null)
                    }
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
              <Col lg={3} md={6} xs={12}>
                <Form.Group className="h-100">
                  <Form.Label className="d-block text-nowrap mb-1">ID Συνεργείου</Form.Label>
                  <Form.Control
                    type="number"
                    value={searchCriteria.GarageID || ""}
                    onChange={e =>
                      handleCriteriaChange("GarageID", e.target.value ? parseInt(e.target.value, 10) : null)
                    }
                    disabled={loading}
                  />
                </Form.Group>
              </Col>
            </>
          )}

          {!isGarage() && (
            <Col lg={3} md={6} xs={12}>
              <Form.Group className="h-100">
                <Form.Label className="d-block text-nowrap mb-1">Αρ. Κυκλοφορίας</Form.Label>
                <Form.Control
                  type="text"
                  value={searchCriteria.VehiclePlateNumber}
                  onChange={e => handleCriteriaChange("VehiclePlateNumber", e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          )}

          <Col xs={12} className="mt-4">
            <div className="d-flex justify-content-between">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Αναζήτηση...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Αναζήτηση
                  </>
                )}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={loading}>
                <i className="fas fa-undo me-2" />
                Επαναφορά
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <hr className="my-4" />

      {/* Results Section */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Φόρτωση...</span>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">
          <i className="fas fa-exclamation-circle fa-2x mb-3" />
          <p className="mb-0">Παρουσιάστηκε σφάλμα κατά την αναζήτηση.</p>
        </div>
      ) : repairs.length > 0 ? (
        <DataTable
          data={repairs}
          loading={loading}
          error={error}
          onRowClick={item => handleViewRepair(item.RepairID)}
          columns={[
            {
              header: "ID Επισκευής",
              field: "RepairID",
              type: "number"
            },
            { header: "Αρ. Φακέλου", field: "ClaimNo", type: "string" },
            {
              header: "Γκαράζ",
              field: "GarageName",
              type: "string"
            },
            {
              header: "Ημ.Καταχώρησης",
              field: "IssueDateTime",
              type: "date",
              render: item => formatDateForDisplay(item.IssueDateTime)
            },
            {
              header: "Status",
              field: "Status",
              type: "number",
              render: item => (
                <span className={`badge bg-status-${item.Status}`}>{getStatusLabel(item.Status)}</span>
              ),
              sortValue: item => item.Status
            },
            {
              header: "Πραγματογνώμονας",
              field: "ExpertName",
              type: "string"
            },
            {
              header: "Ημ. Ολοκλήρωσης",
              field: "CompletionDateTime",
              type: "date",
              render: item => formatDateForDisplay(item.CompletionDateTime)
            },
            {
              header: "Ιδιοκτήτης",
              field: "InsuredName",
              type: "string"
            },
            {
              header: "Αρ. Κυκλοφορίας",
              field: "PlateNumber",
              type: "string"
            }
          ]}
        />
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-search fa-2x mb-3" />
          <p className="mb-0">Δεν βρέθηκαν αποτελέσματα που να αντιστοιχούν στα κριτήρια αναζήτησης.</p>
        </div>
      ) : null}
    </Layout>
  )
}

export default RepairsList
